import React, { FC, useEffect } from "react";
import reportWebVitals from "./reportWebVitals";
import ReactDOM from "react-dom";
import * as Sentry from "@sentry/react";
import TagManager from "react-gtm-module";
import client from "./apollo-client";
import { ApolloProvider } from "@apollo/client";
import { signInAnonymously } from "firebase/auth";

import { usePergola, DimensionsOperations } from "./data-stores/pergola";
import { auth } from "./firebase";
import { useUser } from "./data-stores/user";
import { StructureColor, CanopyColor } from "./configurations/colors";
import { dimensionsMinMaxValues } from "./configurations/dimensions";

import App from "./App";
import { MediaQueryContextProvider } from "./contexts/MediaQueryContext";
import {
  createBrowserRouter,
  RouterProvider,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { updateConfiguration } from "./firebase/firestore/configurations";
import { Loader } from "./components/shared/Loader";
import "./index.scss";
import { BrowserTracing } from "@sentry/tracing";
import { functionsService } from './services/functions-service';

const tagManagerArgs = {
  gtmId: "GTM-MHJ4LWD",
};

TagManager.initialize(tagManagerArgs);

Sentry.init({
  dsn: "https://b2ab8b8bd4b34807857599eb7801c7ab@o479719.ingest.sentry.io/6260795",
  integrations: [new BrowserTracing()],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
});

// Helper function to parse size parameter (e.g., "12x14" -> {length: 12, width: 14})
const parseSizeParameter = (size: string) => {
  const parts = size.split("x").map((part) => parseInt(part, 10));
  if (parts.length === 2 && parts.every((part) => !isNaN(part))) {
    const [length, width] = parts;
    return { length, width };
  }
  return null;
};

// Helper function to validate and clamp dimensions
const validateDimension = (
  value: number,
  dimension: "height" | "length" | "width"
) => {
  const min = dimensionsMinMaxValues[dimension].min.feet;
  const max = dimensionsMinMaxValues[dimension].max.feet;
  return Math.max(min, Math.min(max, value));
};

// Helper function to parse structure color
const parseStructureColor = (color: string): StructureColor | null => {
  const colorMap: { [key: string]: StructureColor } = {
    "charcoal-gray": StructureColor.CHARCOAL_GRAY,
    charcoalgray: StructureColor.CHARCOAL_GRAY,
    gray: StructureColor.CHARCOAL_GRAY,
    white: StructureColor.HAMPTON_WHITE,
    "hampton-white": StructureColor.HAMPTON_WHITE,
    hamptonwhite: StructureColor.HAMPTON_WHITE,
    "brazilian-ipe": StructureColor.BRAZILIAN_IPE,
    brazilianipe: StructureColor.BRAZILIAN_IPE,
    ipe: StructureColor.BRAZILIAN_IPE,
    "tuscan-cedar": StructureColor.TUSCAN_CEDAR,
    tuscancedar: StructureColor.TUSCAN_CEDAR,
    cedar: StructureColor.TUSCAN_CEDAR,
  };
  return colorMap[color.toLowerCase()] || null;
};

// Helper function to parse canopy color
const parseCanopyColor = (color: string): CanopyColor | null => {
  const colorMap: { [key: string]: CanopyColor } = {
    cappuccino: CanopyColor.CAPPUCCINO,
    "dove-white": CanopyColor.DOVE_WHITE,
    dovewhite: CanopyColor.DOVE_WHITE,
    "sterling-silver": CanopyColor.STERLING_SILVER,
    sterlingsilver: CanopyColor.STERLING_SILVER,
    "desert-sand": CanopyColor.DESERT_SAND,
    desertsand: CanopyColor.DESERT_SAND,
    "pacific-blue": CanopyColor.PACIFIC_BLUE,
    pacificblue: CanopyColor.PACIFIC_BLUE,
    "sand-storm": CanopyColor.CLOUD_NINE,
    sandstorm: CanopyColor.CLOUD_NINE,
    cloudnine: CanopyColor.CLOUD_NINE,
    "cloud-nine": CanopyColor.CLOUD_NINE,
  };
  return colorMap[color.toLowerCase()] || null;
};

const ConfigurationLoader: FC = () => {
  const { user, setUser } = useUser((state) => state);
  const updateConfigurationID = usePergola(
    (state) => state.updateConfigurationID
  );
  const navigate = useNavigate();
  const location = useLocation();
  const updateBuildNumber = usePergola(
    (state) => state.updatePergolaBuildNumber
  );

  // Pergola store methods for setting initial values
  const changePergolaColor = usePergola((state) => state.changePergolaColor);
  const changeLengthFeet = usePergola((state) => state.changeLengthFeet);
  const changeWidthFeet = usePergola((state) => state.changeWidthFeet);
  const changeLengthInches = usePergola((state) => state.changeLengthInches);
  const changeWidthInches = usePergola((state) => state.changeWidthInches);
  const changeHeightFeet = usePergola((state) => state.changeHeightFeet);
  const changeHeightInches = usePergola((state) => state.changeHeightInches);
  const toggleHasCanopy = usePergola((state) => state.toggleHasCanopy);
  const changeCanopyColor = usePergola((state) => state.changeCanopyColor);
  const hasCanopy = usePergola((state) => state.canopy.hasCanopy);

  useEffect(() => {
    if (user) {
      return;
    }

    (async () => {
      try {
        const user = await signInAnonymously(auth);
        setUser(user);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [setUser, user]);

  useEffect(() => {
    if (!user) {
      return;
    }

    (async () => {
      // Parse URL parameters for initial pergola configuration
      const searchParams = new URLSearchParams(location.search);
      const sizeParam = searchParams.get("size");
      const structureParam = searchParams.get("structure");
      const canopyParam = searchParams.get("canopy");

      // console.log("Url Params", sizeParam, structureParam, canopyParam);

      // Set initial dimensions if size parameter is provided
      if (sizeParam) {
        const parsedSize = parseSizeParameter(sizeParam);
        // console.log("Parsed size", parsedSize);
        if (parsedSize) {
          const validatedLength = validateDimension(
            parsedSize.length,
            "length"
          );
          const validatedWidth = validateDimension(parsedSize.width, "width");

          changeLengthFeet(DimensionsOperations.VALUE, validatedLength);
          changeLengthInches(DimensionsOperations.VALUE, 0);
          changeWidthFeet(DimensionsOperations.VALUE, validatedWidth);
          changeWidthInches(DimensionsOperations.VALUE, 0);
          changeHeightFeet(DimensionsOperations.VALUE, 9);
          changeHeightInches(DimensionsOperations.VALUE, 10);
        }
      }

      // Set initial structure color if structure parameter is provided
      if (structureParam) {
        const parsedStructureColor = parseStructureColor(structureParam);
        // console.log("Parsed structure color", parsedStructureColor);
        if (parsedStructureColor) {
          changePergolaColor(parsedStructureColor);
        }
      }

      // Set initial canopy settings if canopy parameter is provided
      if (canopyParam) {
        const parsedCanopyColor = parseCanopyColor(canopyParam);
        // console.log("Parsed canopy color", parsedCanopyColor);
        if (parsedCanopyColor) {
          // Enable canopy if not already enabled
          if (!hasCanopy) {
            toggleHasCanopy();
          }
          changeCanopyColor(parsedCanopyColor);
        }
      }

      const { buildNumber } = await functionsService.createConfigurationWithBuildID(
        user.user.uid
      );
      const newConfigurationID = buildNumber;

      updateConfigurationID(newConfigurationID);
      updateBuildNumber(buildNumber);

      // Save the initial configuration values to Firestore if any URL parameters were provided
      const hasInitialValues = sizeParam || structureParam || canopyParam;
      if (hasInitialValues) {
        // Get current pergola state to save to Firestore
        const currentState = usePergola.getState();

        await updateConfiguration(newConfigurationID, {
          design: {
            pergola: currentState.design.pergola,
            color: currentState.design.color,
          },
          dimensions: {
            height: {
              feet: currentState.dimensions.height.feet,
              inches: currentState.dimensions.height.inches,
            },
            length: {
              feet: currentState.dimensions.length.feet,
              inches: currentState.dimensions.length.inches,
            },
            width: {
              feet: currentState.dimensions.width.feet,
              inches: currentState.dimensions.width.inches,
            },
          },
          canopy: {
            hasCanopy: currentState.canopy.hasCanopy,
            color: currentState.canopy.color,
          },
          beams: currentState.beams,
          collaborative: currentState.collaborative,
          updatedBy: user.user.uid,
        });
      }

      navigate(`/${newConfigurationID}`);
    })();
  }, [
    navigate,
    updateBuildNumber,
    updateConfigurationID,
    user,
    location.search,
    changePergolaColor,
    changeLengthFeet,
    changeWidthFeet,
    toggleHasCanopy,
    changeCanopyColor,
    hasCanopy,
    changeHeightFeet,
    changeHeightInches,
    changeLengthInches,
    changeWidthInches,
  ]);

  return <Loader />;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <ConfigurationLoader />,
  },
  {
    path: "/:buildNumber",
    element: <App />,
  },
]);

ReactDOM.render(
  <React.StrictMode>
    <ApolloProvider client={client}>
      <MediaQueryContextProvider>
        <RouterProvider router={router} />
      </MediaQueryContextProvider>
    </ApolloProvider>
  </React.StrictMode>,
  document.getElementById("root")
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.
// ly/CRA-vitals
reportWebVitals();

// First we get the viewport height and we multiple it by 1% to get a value for a vh unit
let vh = window.innerHeight * 0.01;
// Then we set the value in the --vh custom property to the root of the document
document.documentElement.style.setProperty("--vh", `${vh}px`);
